import React, {
  CSSProperties,
  useEffect,
  useState
} from 'react'

import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'

import {
  Controller,
  useForm,
} from 'react-hook-form'

import Dropzone, {
  IFileWithMeta,
  StatusValue,
} from 'react-dropzone-uploader'
import CustomPreview from './dropzone/CustomPreview'
import 'react-dropzone-uploader/dist/styles.css'
// Implementation: https://academy.ilyakam.com/control-react-dropzone-uploader-with-react-hook-form-99e8419546d6

import { serialize } from 'object-to-formdata'
import reactToWebComponent from 'react-to-webcomponent'

import RpTextArea from './TextArea'

const FormComponent = ({ prompts, neighborhoods, confirmation, guidelines }) => {
  // props
  try {
    prompts = JSON.parse(prompts)
    neighborhoods = JSON.parse(neighborhoods)
  } catch (e) {
    prompts = []
    neighborhoods = []
  }

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
                  'August', 'September', 'October', 'November', 'December']
  const fileSizeMax = 5 * 1024 * 1024 // 5mb

  const [residency, setResidency] = useState(false)
  const [duration, setDuration] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  // use form hooks
  const {
    register, 
    unregister,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isSubmitting, isSubmitted }
  } = useForm({ defaultValues: { files: [] } })

  // on submit
  const onSubmit = async (data) => {
    const files = data.files.slice()
    const postdata = Object.assign({}, data)
    delete postdata.files
    
    // generate formdata object and append file(s)
    const formdata = serialize(postdata)
    files.forEach(file => {
      if (file.file.size <= fileSizeMax) { // ensure filesize
        formdata.append('file[]', file.file)      
      }
    })

    const response = await fetch('/contribute/add', {
      method: 'POST',
      body: formdata
    })

    const jsonData = await response.json()

    if (jsonData.success) {
      window.location.href =
        window.location.protocol + "//" +
        window.location.host +
        window.location.pathname +
        '?submitted';
      // 1. set has submitted
      // setHasSubmitted(true)

      // 2. scroll to top
      // window.scroll({ top: 0, behavior: 'smooth' })

      // 3. reset form
      // reset()
    }
  }

  // catch to update files
  const handleControlledDropzonChangeStatus = (status, allFiles, setFiles) => {
    setTimeout(() => {
      if (['done', 'removed'].includes(status)) {
        setFiles([...allFiles])
      }
    }, 0)
  }

  useEffect(() => {
    if (!residency) unregister('residency.neighborhood')
    if (!duration) unregister('residency.duration')
  }, [residency, duration, unregister])

  // remove current submitted query param
  
  history.replaceState({}, null, window.location.pathname)

  return (
    <form className="w-full space-y-12 md:space-y-20" onSubmit={handleSubmit(onSubmit)}>

      {/* Currently Unused: */}
      {isSubmitted && <div className="w-full md:w-10/12 lg:w-8/12 mx-auto" dangerouslySetInnerHTML={{__html: confirmation || 'Thank you for your submission!' }}></div>}
      
      {/* Prompt Field */}
      <fieldset>
        <legend className="block w-full mb-6 with-required">Choose a prompt to respond to</legend>
        <div className="grid grid-cols-radio auto-rows-fr gap-4 sm:gap-7" data-component="monogrid">
          {prompts.map(prompt => {
            let style = { "--accent": prompt.color } as CSSProperties
            return (
              <label className="flex items-center radio-block" key={prompt.category}>
                <input {...register("prompt", { required: true })} type="radio" className="sr-only" value={prompt.category} />
                <div className="rounded-3xl border border-accent text-base sm:text-md pt-8 pb-7 px-10 cursor-pointer w-full h-full flex justify-center items-center text-center" style={style}>{prompt.prompt}</div>
              </label>
            )
          })}
        </div>
        {errors.prompt && <div className="pt-6 text-sm text-red-600 uppercase">This field is required</div>}
      </fieldset>

      <div className="w-full md:w-10/12 lg:w-8/12 xl:w-7/12 2xl:w-1/2 mx-auto text-left space-y-12 md:space-y-14">

      {/* Neighborhood Field */}
      <label className="block">
        <div className="mb-3 with-required">In which Rail Park neighborhood does this moment happen?</div>
        <select className="
            text-mobile sm:text-md pt-4 pb-3 px-5 block w-full rounded-full border-gray-400 shadow-sm
            focus:border-gray-400 focus:ring focus:ring-gray-300 focus:ring-opacity-50
        " data-component="select" {...register("neighborhood", { required: true })}>
          <option value="">Select Neighborhood…</option>
          {neighborhoods.map(neighborhood => (
            <option value={neighborhood.autoid} key={neighborhood.autoid}>{neighborhood.title}</option>
          ))}
        </select>
        {errors.neighborhood && <div className="pt-3 text-sm text-red-600 uppercase">This field is required</div>}
      </label>

      {/* Description Field */}
      <Controller
        control={control}
        name="description"
        rules={{ required: true }}
        render={({ field, fieldState: { invalid, isTouched, isDirty, error } }) => (
          <RpTextArea max={200} field={field} error={error} />
        )} 
      />

      {/* Date */}
      <fieldset className="grid grid-cols-4 gap-3">
        <div className="col-span-full"><legend>When does this moment take place?</legend></div>
        <input type="text" className="
          col-span-full sm:col-span-1 text-mobile sm:text-md pt-4 pb-3 px-5 block w-full rounded-full border-gray-400 placeholder-gray-400 shadow-sm
          focus:border-gray-400 focus:ring focus:ring-gray-300 focus:ring-opacity-50
        " placeholder="Year" {...register("year")} autoComplete="off" />
        <select className="
          col-span-full sm:col-span-2 text-mobile sm:text-md pt-4 pb-3 px-5 block w-full rounded-full border-gray-400 shadow-sm
          focus:border-gray-400 focus:ring focus:ring-gray-300 focus:ring-opacity-50
        " data-component="select" {...register("month")}>
          <option value="">Month</option>
          {months.map((month, i) => (
            <option value={i + 1} key={month}>{month}</option>
          ))}
        </select>
        <input type="number" className="
          col-span-full sm:col-span-1 text-mobile sm:text-md pt-4 pb-3 px-5 block w-full rounded-full border-gray-400 placeholder-gray-400 shadow-sm
          focus:border-gray-400 focus:ring focus:ring-gray-300 focus:ring-opacity-50
        " placeholder="Day" {...register("day")} autoComplete="off" min="1" max="31" />
      </fieldset>

      {/* Address */}
      <label className="block">
        <div className="mb-3 with-required">Where does this moment take place?</div>
        <input type="text" className="
          text-mobile sm:text-md pt-4 pb-3 px-5 block w-full rounded-full border-gray-400 placeholder-gray-400 shadow-sm
          focus:border-gray-400 focus:ring focus:ring-gray-300 focus:ring-opacity-50
        " placeholder="Enter address or location" {...register("address", { required: true })} autoComplete="off" />
        {errors.address && <div className="pt-3 text-sm text-red-600 uppercase">This field is required</div>}
      </label>

      {/* Image Field */}
      <div>
        <label className="mb-3 block with-max" data-max="5mb" htmlFor="files">Upload media related to this moment</label>
        <Controller
          control={control}
          name="files"
          id="files"
          autoUpload={false}
          render={({ field: { onChange } }) => (
            <Dropzone
              accept="image/jpg, image/jpeg, image/png, image/gif, video/mp4, audio/mpeg"
              maxFiles={5}
              maxSizeBytes={5 * 1024 * 1024}
              inputContent={
                <div className="text-center" key="rp-input-content">
                  <div className="uppercase">Drag Files or Click to Browse</div>
                  <div>jpg, png, gif, mp4, mp3</div>
                </div>
              }
              addClassNames={{
                dropzone: 'dzu-rp-dropzone',
                inputLabel: 'dzu-rp-inputLabel',
                inputLabelWithFiles: 'dzu-rp-inputLabelWithFiles',
                preview: 'dzu-rp-previewContainer'
              }}
              PreviewComponent={CustomPreview}
              onChangeStatus={(file, status, allFiles) => {
                handleControlledDropzonChangeStatus(status, allFiles, onChange)
              }}
            />
          )}
        />
      </div>

      <hr />

      {/* Name */}
      <fieldset className="grid grid-cols-2 gap-3">
        <div className="col-span-full"><legend className="with-required-email">A bit about yourself</legend></div>
        <input type="text" className="
          col-span-full sm:col-span-1 text-mobile sm:text-md pt-4 pb-3 px-5 block w-full rounded-full border-gray-400 placeholder-gray-400 shadow-sm
          focus:border-gray-400 focus:ring focus:ring-gray-300 focus:ring-opacity-50
        " placeholder="First name" {...register("firstname")} autoComplete="off" />
        <input type="text" className="
          col-span-full sm:col-span-1 text-mobile sm:text-md pt-4 pb-3 px-5 block w-full rounded-full border-gray-400 placeholder-gray-400 shadow-sm
          focus:border-gray-400 focus:ring focus:ring-gray-300 focus:ring-opacity-50
        " placeholder="Last name" {...register("lastname")} autoComplete="off" />
        <input type="email" className="
          col-span-full text-mobile sm:text-md pt-4 pb-3 px-5 block w-full rounded-full border-gray-400 placeholder-gray-400 shadow-sm
          focus:border-gray-400 focus:ring focus:ring-gray-300 focus:ring-opacity-50
        " placeholder="Email address" {...register("email", { required: true })} autoComplete="off" />
        {errors.email && <div className="text-sm text-red-600 uppercase">This field is required</div>}
      </fieldset>

      {/* Residency */}
      <fieldset>
        <div className="mb-3"><legend>Do you live in Philadelphia?</legend></div>
        <label className="flex items-start text-mobile sm:text-md">
          <input {...register("residency.status")} type="radio" 
            className="w-[0.8em] h-[0.8em] mt-[0.1em] text-gray-800 focus:ring-gray-300" 
            value="current"
            onChange={handleResidencyChange}
          />
          <span className="ml-2">Yes, I currently live in Philadelphia</span>
        </label>
        <label className="flex items-start text-mobile sm:text-md">
          <input {...register("residency.status")} type="radio" 
            className="w-[0.8em] h-[0.8em] mt-[0.1em] text-gray-800 focus:ring-gray-300" 
            value="previous"
            onChange={handleResidencyChange}
          />
          <span className="ml-2">No, but I used to live in Philadelphia</span>
        </label>
        <label className="flex items-start text-mobile sm:text-md">
          <input {...register("residency.status")} type="radio" 
            className="w-[0.8em] h-[0.8em] mt-[0.1em] text-gray-800 focus:ring-gray-300" 
            value="visitor"
            onChange={handleResidencyChange}
          />
          <span className="ml-2">No, but I visited Philadelphia</span>
        </label>
        <label className="flex items-start text-mobile sm:text-md">
          <input {...register("residency.status")} type="radio" 
            className="w-[0.8em] h-[0.8em] mt-[0.1em] text-gray-800 focus:ring-gray-300" 
            value=""
            onChange={handleResidencyChange}
          />
          <span className="ml-2">Other</span>
        </label>
      </fieldset>

      {/* Residency Neighborhood */}
      {residency && (
        <fieldset>
          <div className="mb-3"><legend>Which neighborhood?</legend></div>
          <div className="sm:col-count-2">
            {neighborhoods.map(neighborhood => (
              <label className="flex items-start text-mobile sm:text-md" key={neighborhood.autoid}>
                <input {...register("residency.neighborhood")} type="radio" className="w-[0.8em] h-[0.8em] mt-[0.1em] text-gray-800 focus:ring-gray-300" value={neighborhood.autoid} />
                <span className="ml-2">{neighborhood.title}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {/* Residency Duration */}
      {duration && (
        <label className="block">
          <div className="mb-3">How long have you lived in Philadelphia?</div>
          <input {...register("residency.duration")} type="number" className="
            text-mobile sm:text-md pt-4 pb-3 px-5 block w-full rounded-full border-gray-400 placeholder-gray-400 shadow-sm
            focus:border-gray-400 focus:ring focus:ring-gray-300 focus:ring-opacity-50
          " placeholder="Number of years" autoComplete="off" min="0" max="100" />
        </label>
      )}

      <hr />

      {/* Honeypot: /^$/ ensures empty string */}
      <div className="hidden">
        <label className="sr-only">Don’t fill this out if you're human: </label>
        <input {...register("bot-field", { pattern: /^$/ })} placeholder="This field is only for the robots." className="
          text-mobile sm:text-md pt-4 pb-3 px-5 block w-full rounded-full border-gray-400 placeholder-gray-400 shadow-sm
          focus:border-gray-400 focus:ring focus:ring-gray-300 focus:ring-opacity-50
        " />
      </div>

      {/* Guidelines */}
      {guidelines && <div className="text-base space-y-lh links rich-text" dangerouslySetInnerHTML={{__html: guidelines || '' }}></div>}

      {/* Submit */}
      <button className="
        mt-1 text-mobile sm:text-md font-normal pt-4 pb-3 px-5 block w-full rounded-full border border-black shadow-sm hover:bg-black hover:text-white disabled:opacity-50 disabled:pointer-events-none
      " type="submit" disabled={isSubmitting}>{isSubmitting ? 'Submitting…' : 'Submit your moment'}</button>

      </div>
    </form>
  )

  function handleResidencyChange (e) {
    setResidency(e.target.value)
    setDuration((['current', 'previous'].indexOf(e.target.value) >= 0))
  }
}

FormComponent.propTypes = {
  prompts: PropTypes.string.isRequired,
  neighborhoods: PropTypes.string.isRequired,
  confirmation: PropTypes.string,
  guidelines: PropTypes.string
}

const WrappedFormComponent = reactToWebComponent(FormComponent, React, ReactDOM)

customElements.define('submission-form', WrappedFormComponent)